import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Switch from 'react-switch';
import cn from 'classnames';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import OutsideClickHandler from 'react-outside-click-handler';
import Tooltip from '../General/Tooltip';
import './GiftingControlBar.scss';

import { getAnnouncementByType } from '../../Helpers/announcement_helpers';

const GiftingControlBar = props => {
  const {
    tab,
    user,
    handleTabChange,
    createNewEmptyLookbook,
    loadGiftingRecommendations,
    isLoadingRecommendations,
    setCurSearchVal,
    setSelectedFilter,
    curSearchVal,
    isLoading,
    filterOptions
  } = props;

  const [openFilter, setOpenFilter] = useState(false);
  const [shouldExcludeBrandSellers, setShouldExcludeBrandSellers] = useState(false);

  const selectedFilter = _.find(filterOptions, { label: props.selectedFilter?.label }) || filterOptions[0];

  const changeFilter = filterOption => {
    setCurSearchVal('');
    setSelectedFilter(filterOption);
    setOpenFilter(false);
  };

  const resetFilters = e => {
    e.stopPropagation();
    changeFilter(null);
  };

  const changeSearchVal = e => {
    const newSearchVal = e.target.value;
    setCurSearchVal(newSearchVal);
  };

  const handleRefreshRecommendations = async () => {
    await loadGiftingRecommendations(shouldExcludeBrandSellers);
  };

  const toggleExcludeBrandSellers = async () => {
    setShouldExcludeBrandSellers(!shouldExcludeBrandSellers);
    await loadGiftingRecommendations(!shouldExcludeBrandSellers);
  };

  const getNewTalentExplanation = () => {
    return `When enabled, talent who have already driven sales for ${user.profile.name} will not appear in recommendations.`;
  };

  return (
    <div className='gifting-control-bar'>
      <div className='control-bar-section' id='tabs'>
        <div className={cn('tab-container', { active: tab === 'requests' })} onClick={() => handleTabChange('/gifting/requests')}>
          <span className='tab-container-tab'>Gifting Requests</span>
        </div>
        <div className={cn('tab-container', { active: tab === 'recommendations' })} onClick={() => handleTabChange('/gifting/recommendations')}>
          <span className='tab-container-tab'>Recommendations</span>
        </div>
        <div className={cn('tab-container', { active: tab === 'lookbooks' })} onClick={() => handleTabChange('/gifting/lookbooks')}>
          {!!getAnnouncementByType(user, 'LOOKBOOKS') && <span className='new-badge'>NEW</span>}
          <span className='tab-container-tab'>Lookbooks</span>
        </div>
      </div>
      <div className='control-bar-section' id='search-and-filter'>
        {tab === 'requests' && (
          <>
            {!_.every(filterOptions, { count: 0 }) && (
              <>
                <div className='filter-container'>
                  <div className='filter-label' onClick={() => setOpenFilter(!openFilter)}>
                    {selectedFilter?.label !== 'Show All' && <FontAwesomeIcon onClick={resetFilters} icon={faTimesCircle} />}
                    {selectedFilter?.label !== 'Show All' ? selectedFilter?.label : <>Filter Status&nbsp;&nbsp;&nbsp;&nbsp;+</>}
                  </div>
                  {openFilter && (
                    <OutsideClickHandler onOutsideClick={() => setOpenFilter(false)}>
                      <div className='filter-dropdown'>
                        {filterOptions.map(option => {
                          const { label, count } = option;

                          if (!count) return null;
                          return (
                            <div
                              className={cn('filter-option', { active: selectedFilter.label === label })}
                              key={label}
                              onClick={() => changeFilter(option)}
                            >
                              {option.label} ({option.count})
                            </div>
                          );
                        })}
                      </div>
                    </OutsideClickHandler>
                  )}
                </div>
                <div className='search-container'>
                  <input type='text' defaultValue={curSearchVal} className='gifting-search-input' onChange={changeSearchVal} placeholder='Search' />
                  <FontAwesomeIcon icon={faSearch} />
                </div>
              </>
            )}
          </>
        )}
        {tab === 'lookbooks' && (
          <>
            <div className={cn('add-button', { isLoading })} onClick={createNewEmptyLookbook}>
              + New Lookbook
            </div>
          </>
        )}
        {tab === 'recommendations' && (
          <>
            <div className='recommendation-actions'>
              <div className='recommendation-control-switch'>
                <div className='recommendation-control-label'>
                  New Talent Only
                  <Tooltip className='info-tooltip' message={getNewTalentExplanation()} getIconDiv={() => <FontAwesomeIcon icon={faInfoCircle} />} />
                </div>
                <Switch
                  onChange={toggleExcludeBrandSellers}
                  checked={shouldExcludeBrandSellers}
                  height={14}
                  width={24}
                  onColor='#333'
                  offColor='#ccc'
                  className='switch'
                  checkedIcon={false}
                  uncheckedIcon={false}
                />
              </div>
              <button
                className={cn('recommendation-control-button', { disabled: isLoadingRecommendations })}
                onClick={handleRefreshRecommendations}
                disabled={isLoadingRecommendations}
              >
                <FontAwesomeIcon icon={faSyncAlt} className={cn({ 'fa-spin': isLoadingRecommendations })} />
                Refresh
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

GiftingControlBar.propTypes = {
  user: PropTypes.object.isRequired,
  tab: PropTypes.string.isRequired,
  handleTabChange: PropTypes.func.isRequired,
  selectedFilter: PropTypes.object,
  loadGiftingRecommendations: PropTypes.func.isRequired,
  isLoadingRecommendations: PropTypes.bool.isRequired,

  curSearchVal: PropTypes.string,
  setCurSearchVal: PropTypes.func.isRequired,
  setSelectedFilter: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  filterOptions: PropTypes.array.isRequired,

  createNewEmptyLookbook: PropTypes.func.isRequired
};

export default GiftingControlBar;
